import { useState } from 'react';
import { findPushPlatform } from '@/features/push-credentials';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useAnalytics } from '@/services/analytics';
import { FcmProviderForm } from '../components/fcm-provider-form';
import { AndroidProviderCard } from '../components/provider-chooser';
import { PushPlatformEmptyState } from '../components/push-platform-empty-state';
import { PushSettingsLoading } from '../components/push-settings-loading';
import {
  usePushConfigurationViewedAnalytics,
  usePushCredentials,
  usePushPermissions,
  usePushPlatformBreadcrumb,
  usePushPlatforms,
  type PushCredentialsState,
  type PushPlatformsState,
} from '../hooks';
import type { PushSaveContext } from '../types';

type AndroidPushConfigProps = {
  workspaceId: number;
  platformsState: PushPlatformsState;
  credentials: PushCredentialsState;
};

function AndroidPushConfig({ workspaceId, platformsState, credentials }: AndroidPushConfigProps) {
  const { track } = useAnalytics();
  const { canCreate, canEdit, canDelete } = usePushPermissions();
  const { platforms, providers } = platformsState;

  const isConfigured = findPushPlatform(platforms, 'android') != null;
  const [isEditing, setIsEditing] = useState(false);
  const showEmptyState = !isConfigured && !isEditing;

  usePushConfigurationViewedAnalytics('android', 'fcm', credentials.fcm != null, !showEmptyState);

  if (showEmptyState) {
    return (
      <PushPlatformEmptyState platform="android" canCreate={canCreate} onStartConfiguring={() => setIsEditing(true)} />
    );
  }

  // When one Firebase key is serving every platform the workspace has, removing
  // it is not this tab's decision — the page header takes it away for both.
  const isSharedAcrossPlatforms = platforms.length > 1 && platforms.every((entry) => entry.provider === 'fcm');

  return (
    <FcmProviderForm
      workspaceId={workspaceId}
      platform="android"
      platforms={platforms}
      providers={providers}
      credential={credentials.fcm}
      isEditing={isEditing}
      isReading={platformsState.isFetching || credentials.isFetching}
      canEdit={canEdit}
      canDelete={canDelete}
      showDisable={isConfigured && !isSharedAcrossPlatforms}
      chooser={<AndroidProviderCard />}
      onEditingChange={setIsEditing}
      onCancel={() => setIsEditing(false)}
      onSaved={({ wasConfigured }: PushSaveContext) => {
        void track('push', wasConfigured ? 'authorizationUpdated' : 'authorizationAdded', {
          platform: 'Android',
          provider: 'FCM',
        });
      }}
      onDisabled={() => {
        void track('push', 'pushDisabled', { platform: 'Android', provider: 'FCM' });
      }}
    />
  );
}

type AndroidPushTabProps = {
  workspaceId: number;
};

function AndroidPushTab({ workspaceId }: AndroidPushTabProps) {
  const platformsState = usePushPlatforms(workspaceId);
  const credentials = usePushCredentials(workspaceId);

  if (platformsState.isPending || credentials.isPending) {
    return <PushSettingsLoading />;
  }

  return <AndroidPushConfig workspaceId={workspaceId} platformsState={platformsState} credentials={credentials} />;
}

export default function WorkspacePushAndroidSettingsPage() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  usePushPlatformBreadcrumb(workspaceId, 'android');

  if (workspaceId == null || workspaceId <= 0) {
    return <PushSettingsLoading />;
  }

  return <AndroidPushTab workspaceId={workspaceId} />;
}
